import React, { useState, useEffect } from 'react'
import { useMutation } from 'react-apollo'
import { useHistory, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { Background } from 'react-imgix'
import ReactSVG from 'react-svg'

import AUTHORISE_PAIR_SESSION_MUTATION from '../../../graphql/mutations/authorise-pair-session.gql'
import Button from '../button'
import Input from '../forms/input'
import Loader from '../loader'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import { useConfig } from '../../hooks/use-config'
import { getConfigValue } from '../../utils/getConfigValue'
import { getModalLocation } from '../../lib/modal'
import {
  MODALS, FORM_MESSAGES, IMGIX_CROP_OPTIONS, APPLE_DOWNLOAD_URL, GOOGLE_DOWNLOAD_URL
} from '../../constants'
import { displayErrorDialog } from '../../actions'
import { getGQLErrorMsg } from '../../lib/apollo'
import tvIcon from '../../../images/tv-icon.svg'
import NeonLogo from '../../../images/neon/neon-logo-yellow.svg'
import AppleStore from '../app-download/apple-store.svg'
import GoogleStore from '../app-download/google-store.svg'
import styles from './tv-easy-login.css'

/**
 * Handle TV Easy Login for the following scenarios:
 * 1. User scans QR code → URL contains pairCode → confirm pairing
 * 2. User navigates manually → enter pair code manually
 * 3. User must be authenticated before pairing
 * 4. Shows confirmation view after pairing successfully
 */
function TVEasyLogin() {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const { appConfig, configLoading } = useConfig()
  const isAuthenticated = useIsAuthenticated()
  const [isCodeConfirmed, setIsCodeConfirmed] = useState(false)
  const [authorisePairSessionMutation,
    { loading: authorisePairSessionLoading }
  ] = useMutation(AUTHORISE_PAIR_SESSION_MUTATION)
  const confirmImage = getConfigValue(appConfig?.values, 'GUEST_HOME_HOTSPOT_IMAGE')
  const params = new URLSearchParams(location.search)
  const pairCodeFromURL = params.get('pairCode') || null

  // Redirect to login model if user is not authenticated
  useEffect(() => {
    if (!isAuthenticated && params.get('modal') !== 'login') {
      history.push(getModalLocation(
        location,
        MODALS.qsParams.login
      ))
    }
  }, [isAuthenticated])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      code: ''
    },
    mode: 'onChange'
  })

  const onSubmit = async (values) => {
    try {
      const { data } = await authorisePairSessionMutation({
        variables: {
          pairCode: values.code || pairCodeFromURL
        }
      })

      if (data?.authorisePairSession === 'SUCCESS') {
        // Display the confirmation view and clear URL params
        setIsCodeConfirmed(true)
        history.replace({ pathname: location.pathname, search: '' })
      }

      if (data?.authorisePairSession === 'INVALID_OR_EXPIRED_CODE') {
        // Show popup error message
        dispatch(displayErrorDialog({ message: 'That code isn’t valid or has expired. Please rescan the QR code or enter the 6 character code that’s displayed on your TV.' }))
        // Clear the input value
        reset({ code: '' })
        // Remove all params from URL (e.g. pairCode) while keeping the route ('/tv-login') for user to manually enter a new code
        history.replace({ pathname: location.pathname, search: '' })
      }
    } catch (error) {
      // Handle gql error
      dispatch(displayErrorDialog({ message: getGQLErrorMsg(error) }))
    }
  }

  const unauthenticatedView = () => {
    return (
      <>
        <span className={styles.title}>
          Please login here first
        </span>

        <span className={styles.subTitle}>
          The code from your TV display will appear here once you’ve logged into your account.
        </span>
      </>
    )
  }

  // If pairCode is existing in url
  const preloadCodeView = () => {
    return (
      <>
        <span className={styles.title}>
          Confirm this code matches your TV
        </span>

        <span className={styles.code}>{pairCodeFromURL}</span>

        <Button
          className={styles.button}
          text="Confirm"
          onClick={onSubmit}
          disabled={authorisePairSessionLoading}
        />

        <span className={styles.message}>
          If this code doesn’t match, close this window and rescan the QR code on your TV.
          &nbsp;
        </span>
      </>
    )
  }

  const manualCodeEntryView = () => {
    return (
      <>
        <span className={styles.title}>
          Enter the code displayed on your TV
        </span>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="code"
            control={control}
            rules={{
              required: FORM_MESSAGES.required,
              pattern: {
                value: /^[A-Z0-9]{6}$/,
                message: 'Please enter the 6 character code shown on your TV'
              }
            }}
            render={({ field }) => (
              <Input
                label=""
                name={field.name}
                inputClass={styles.input}
                checkClass={styles.check}
                exclamationClass={styles.exclamation}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                onBlur={field.onBlur}
                warning={errors.code?.message}
                maxLength={6}
                isValid={Boolean(field.value && !errors.code)}
                placeholder="Code"
              />
            )}
          />

          <Button
            text="Confirm"
            className={styles.button}
            disabled={authorisePairSessionLoading}
          />
        </form>
      </>
    )
  }

  const codeConfirmedView = () => {
    return (
      <>
        <img className={styles.icon} src={tvIcon} />
        <span className={styles.title}>You’ve paired your TV!</span>
        <span className={styles.subTitle}>
          You’re now logged in on your TV which will automatically update now. Start watching instantly.
        </span>
        <div className={styles.imageWrapper}>
          <Background
            className={styles.image}
            type="img"
            src={confirmImage}
            imgixParams={{
              crop: IMGIX_CROP_OPTIONS.HOTSPOT,
              fit: 'crop',
              w: 650,
              h: 310
            }}
            precision={50}
          />
          <div className={styles.topGradient} />
          <div className={styles.overlayContent}>
            <ReactSVG src={NeonLogo} alt="Neon Logo" />
            <span className={styles.overlayTitle}>Watch on more devices</span>
            <span className={styles.overlayText}>Download the NEON App on your mobile or tablet</span>
            <div className={styles.downloadsContainer}>
              <a href={APPLE_DOWNLOAD_URL} className={styles.download}>
                <ReactSVG src={AppleStore} />
              </a>
              <a href={GOOGLE_DOWNLOAD_URL} className={styles.download}>
                <ReactSVG src={GoogleStore} />
              </a>
            </div>
          </div>
        </div>
      </>
    )
  }

  let viewState = null

  if (!isAuthenticated) {
    viewState = 'UNAUTHENTICATED'
  } else if (isCodeConfirmed) {
    viewState = 'CONFIRMED'
  } else if (pairCodeFromURL) {
    viewState = 'PRELOAD_CODE'
  } else {
    viewState = 'MANUAL'
  }

  if (viewState === 'CONFIRMED' && (configLoading || !confirmImage)) {
    return <Loader />
  }

  return (
    <div className={styles.container}>
      {viewState === 'UNAUTHENTICATED' && unauthenticatedView()}
      {viewState === 'PRELOAD_CODE' && preloadCodeView()}
      {viewState === 'MANUAL' && manualCodeEntryView()}
      {viewState === 'CONFIRMED' && codeConfirmedView()}
    </div>
  )
}

export default TVEasyLogin
